<template>
  <PDropdown
    placement="bottom-end"
    color="primary"
    :version="2"
    :same-width="false"
    @open="isDropDownOpen = true"
    @close="isDropDownOpen = false"
    :mobile="true"
    class="[&>[role=menu]]:bg-transparent [&>[role=menu]]:backdrop-blur-lg"
  >
    <template #icon>
      <span class="relative">
        <OhVueIcon :name="bellIcon"></OhVueIcon>
        <span
          v-if="totalUnread > 0"
          class="absolute top-0 right-0 font-bold text-xs text-white bg-red-marianne-425-active border-white border-[1px] rounded-full w-1 h-1 flex items-center justify-center"
        >
        </span>
      </span>
    </template>

    <template #label>
      <span class="font-medium">{{ getLabel() }}</span>
    </template>

    <template v-slot="{ close }">
      <div class="lg:w-[26rem]">
        <div v-if="store.data.length" class="flex flex-col">
          <div class="py-1 px-2 order-last">
            <div class="flex gap-1 justify-end">
              <PButton
                size="sm"
                color="error"
                mode="tertiary-borderless"
                :disabled="!store.data.length"
                @click="removeAll()"
              >
                Tout supprimer
              </PButton>
              <PButton
                @click="markAllAsRead()"
                mode="secondary"
                size="sm"
                :disabled="store.data.every(item => item.isRead)"
              >
                Tout marquer comme lu
              </PButton>
            </div>
          </div>
          <div class="overflow-y-auto overflow-x-hidden max-h-[45vh]" @scroll="onScroll">
            <div
              class="group relative"
              v-for="item of store.data"
              :key="item.id"
              :class="{
                'hover:bg-grey-975': !item.action && item.isRead,
                'bg-info-950/80 hover:bg-info-925/50': !item.isRead,
                'hover:bg-info-925/50': item.action && item.isRead,
              }"
            >
              <component
                :is="getComponent(item)"
                :to="item.action"
                class="w-full -outline-offset-2 text-left"
                @click="clickOnNotif(item, close)"
              >
                <template #icon><div></div></template>
                <div class="px-2 pt-2 pb-1">
                  <div class="flex justify-between items-center gap-2">
                    <div class="flex items-center">
                      <OhVueIcon v-if="item.action" class="mr-1 text-blue-france-sun-113" :name="arrow"></OhVueIcon>

                      <span class="line-clamp-2" :class="{ 'text-blue-france-sun-113': item.action }">{{
                        item.mainMessage
                      }}</span>
                    </div>
                    <div v-if="!item.isRead" class="w-2 h-2 shrink-0 rounded-full bg-info"></div>
                  </div>

                  <p class="text-sm italic text-grey-125">
                    {{ item.secondaryMessage }}
                  </p>

                  <div class="flex justify-between items-center gap-2">
                    <p class="text-sm text-grey-100">
                      {{ dayjs(item.creationDate).format("Le DD/MM/YYYY à HH[h]mm") }}
                    </p>
                  </div>
                </div>
              </component>
              <PButton
                class="absolute bottom-1v right-1 group-hover:visible invisible"
                color="error"
                mode="tertiary-borderless"
                size="icon"
                @click="deleteById(item.id)"
                @click.stop
              >
                <OhVueIcon :name="deleteIcon"></OhVueIcon>
              </PButton>
              <div class="border-b border-grey-850 mx-2"></div>
            </div>
            <div class="w-full flex justify-center">
              <PSpinner :enabled="loading" class="m-1" />
            </div>
          </div>
        </div>
        <div v-else>
          <div class="p-2 flex items-center justify-center">
            <span>Aucune notification</span>
          </div>
        </div>
      </div>
    </template>
  </PDropdown>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { useAppNotificationStore } from "./store/app-notification.store";
import dayjs from "dayjs";
import { PButton, PDropdown, timeout, useIcons, PLink, PSpinner } from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import { OiBell, RiArrowRightLine, RiDeleteBin6Line } from "oh-vue-icons/icons";
import { type AppNotificationDto } from "./dto/app-notification.dto";
import { useAppNotificationEvents } from "./composables/useAppNotificationEvents";

const [arrow, deleteIcon, bellIcon] = useIcons(RiArrowRightLine, RiDeleteBin6Line, OiBell);

useAppNotificationEvents();

const store = useAppNotificationStore();

const totalUnread = computed(() => store.unread);

const isDropDownOpen = ref(false);

function getLabel() {
  if (totalUnread.value > 0) {
    return `Notification (${totalUnread.value})`;
  }

  return "Notification";
}

function getComponent(item: AppNotificationDto) {
  if (item.action) {
    return PLink;
  }
  if (!item.isRead) {
    return "button";
  }
  return "div";
}

async function clickOnNotif(item: AppNotificationDto, closeFn: () => void) {
  if (!item.isRead) {
    await store.markAsRead(item.id);
  }
  if (item.action) {
    closeFn();
  }
}

async function deleteById(id: number) {
  await store.delete(id);
}

async function markAllAsRead() {
  await store.markAllAsRead();
}

async function removeAll() {
  await store.removeAll();
  await fetch(1);
}

async function fetch(page: number) {
  if (loading.value) return;
  try {
    loading.value = true;
    await Promise.all([store.getMine(page), timeout(500)]);
  } finally {
    loading.value = false;
  }
}

const page = ref(1);
const loading = ref(false);
async function onScroll(e: Event) {
  const { scrollTop, offsetHeight, scrollHeight } = e.target as HTMLElement;
  if (scrollTop + offsetHeight >= scrollHeight) {
    // reached bottom
    page.value = page.value + 1;
    await fetch(page.value);
  } else if (scrollTop === 0) {
    // reached top
    page.value = 1;
    await fetch(1);
  }
}

onMounted(async () => {
  await fetch(1);
});
</script>
